import React, {
  useState,
  useEffect,
  useRef,
  ComponentProps,
  useMemo,
} from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import Highcharts, { merge } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import svgToDataURL from 'svg-to-dataurl';
import {
  PortfolioStuffingChartDatum,
  PortfolioStuffingChartProps,
  PortfolioStuffingProps,
} from './PortfolioStuffingAsset';
import {
  IconOnChart,
  rangeSelectorButtonAlignLeftChartClassName,
} from 'components/features/home/assetStuffing/AssetStuffingChart';
import {
  assetTypeKeyToColorCode,
  assetTypeKeyToString,
} from 'components/features/utils/assetType';
import { arrangeDateForChart } from 'components/features/utils/util';
import {
  ChartForPreviewInternalProps,
  ChartForPreviewType,
  deconstructForPreviewSetter,
} from 'components/features/preview-share/useChartRenderer';
import {
  AreaChartLegend,
  LineChartLegend,
  marker,
} from 'components/features/utils/chartUtils';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import {
  AssetHistoryItemsProps,
  ExtraTimeSeriesData,
  TimeSeriesDatum,
} from 'components/features/home/assetStuffing/AssetStuffing';
import { GetUserAssetResult } from 'config/api';
import { AssetType } from 'config/consts';
import { getDate } from 'components/features/utils/converter';
import {
  UsePreviewProps,
  usePreview,
} from 'components/features/preview-share/Preview';
import {
  ChartPreviewState,
  RenderedChartToElement,
  StateController,
  defualtStateForHome,
  stateToOptions,
} from 'components/features/preview-share/PreviewUtils';
import { WithEmptyView } from 'components/features/home/WithEmptyView';

export const dataToSeries = (
  data: PortfolioStuffingChartDatum[]
): Highcharts.SeriesOptionsType[] => {
  return data
    .filter(item => item.items.some(y => !!y.y))
    .map(item => ({
      ...item,
      items: item.items.map(v => ({
        ...v,
        y: typeof v.y === 'number' && v.y < 0 ? 0 : v.y,
      })),
    }))
    .map(item => {
      return {
        assetTypeKey: item.assetTypeKey,
        items: item.items.sort((a, b) => a.x.getTime() - b.x.getTime()),
      };
    })
    .map(datum => {
      return {
        type: 'area',
        color: assetTypeKeyToColorCode(datum.assetTypeKey),
        fillOpacity: 0.6,
        stacking: 'normal',
        name: assetTypeKeyToString(datum.assetTypeKey, true),
        data: datum.items.map(item => {
          return {
            x: arrangeDateForChart(item.x).getTime(),
            y: item.y,
          };
        }),
      };
    });
};

export const PortfolioStuffingChartInternal = ({
  data,
  options: options_,
  forPreviewSetter,
  extraData,
}: PortfolioStuffingChartProps &
  ChartForPreviewInternalProps): React.ReactElement => {
  const { setChartDataURL, setRange } = deconstructForPreviewSetter(
    forPreviewSetter
  );
  const [icons, setIcons] = useState<IconOnChart[]>([]);
  const series = dataToSeries(data);

  const [options, setOptions] = useState<Highcharts.Options>({
    exporting: { enabled: false },
    legend: {
      align: 'left',
      enabled: true,
      verticalAlign: 'top',
      useHTML: true,
      itemDistance: 0,
      y: -15,
      margin: 5,
      itemStyle: {
        fontSize: '10px',
        fontWeight: 'normal',
        color: '#ffffff',
        textOverflow: 'ellipsis',
      },
      maxHeight: 54,
      itemWidth: 105,
      navigation: {
        activeColor: '#fdba74',
        arrowSize: 12,
        style: { color: '#ffffff', x: 20 },
      },
      className: 'override-highcharts-legend common-margin-top',
      labelFormatter: function() {
        if (this instanceof Highcharts.Series) {
          const name = this.name;
          const color = (() => {
            const t = this as unknown;
            if (
              typeof t === 'object' &&
              t &&
              'color' in t &&
              typeof (t as { color: unknown }).color === 'string'
            ) {
              return (t as { color: string }).color;
            }
            return '';
          })();
          if (this.type === 'area') {
            return renderToStaticMarkup(
              <AreaChartLegend color={color} lightOpacity={0.6}>
                {name}
              </AreaChartLegend>
            );
          }
          if (this.type === 'line') {
            return renderToStaticMarkup(
              <LineChartLegend color={color}>{name}</LineChartLegend>
            );
          }
        }
        return '';
      },
    },
    credits: {
      enabled: false,
    },
    rangeSelector: {
      dropdown: 'always',
      x: 20,
      y: 5,
      buttonTheme: {
        fill: 'none',
        stroke: 'none',
        'stroke-width': 0,
        r: 6,
        style: {
          textAlign: 'center',
          color: '#8e9db2',
        },
        states: {
          hover: {
            fill: '#1E293B',
            style: {
              color: 'white',
              fontWeight: 'normal',
            },
          },
          select: {
            fill: '#1E293B',
            style: {
              color: 'white',
              fontWeight: 'normal',
            },
          },
        },
      },
      inputEnabled: false,
      buttons: [
        {
          type: 'week',
          count: 2,
          text: '14日間',
          title: '14日間',
        },
        {
          type: 'month',
          count: 1,
          text: '1ヶ月',
          title: '1ヶ月',
        },
        {
          type: 'month',
          count: 3,
          text: '3ヶ月',
          title: '3ヶ月',
        },
        {
          type: 'month',
          count: 6,
          text: '6ヶ月',
          title: '6ヶ月',
        },
        {
          type: 'year',
          count: 1,
          text: '1年',
          title: '1年',
        },
        {
          type: 'all',
          text: '全期間',
          title: '全期間',
        },
      ],
      selected: forPreviewSetter.tag === 'forGetRange' ? 0 : undefined,
      buttonPosition: { align: 'left' },
    },
    navigator: {
      enabled: false,
    },
    tooltip: {
      distance: -50,
      borderColor: 'transparent',
      useHTML: true,
      outside: true,
      formatter: function(): string {
        const Tooltip = ({
          date,
          children,
        }: {
          date: Date;
          children: React.ReactElement;
        }): React.ReactElement => {
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gap: '2px',
                  gridAutoRows: 'min-content',
                  gridAutoFlow: 'row',
                }}>
                <div style={{ paddingBottom: '1px', fontSize: '8px' }}>
                  {date.getFullYear()}/{date.getMonth() + 1}/{date.getDate()}
                </div>
                {children}
              </div>
            </>
          );
        };
        const TooltipItem = ({
          name,
          color,
          value,
        }: {
          name: string;
          color: string;
          value: number;
        }): React.ReactElement => {
          return (
            <>
              <div
                className="mx-1"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'min-content min-content min-content',
                  placeItems: 'center start',
                  gap: '2px',
                }}>
                <div
                  style={{
                    borderRadius: '50%',
                    height: '5px',
                    width: '5px',
                    gridColumn: 1,
                    backgroundColor: color,
                  }}
                />
                <span style={{ fontSize: '8px', gridColumn: 2 }}>{name}:</span>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '8px',
                    gridColumn: 3,
                  }}>
                  {commaFormat(value / 10000, { numberOfDecimals: 1 })}万円
                </span>
              </div>
            </>
          );
        };
        return renderToStaticMarkup(
          <Tooltip date={new Date(this.x)}>
            {this.points !== undefined && this.points.length <= 8 ? (
              <>
                {(this.points ?? []).map((point, key) => (
                  <TooltipItem
                    name={point.series.name}
                    color={point.color.toString()}
                    value={point.y}
                    key={key}
                  />
                ))}
              </>
            ) : (
              <div
                className="grid"
                style={{ gridTemplateColumns: 'auto auto' }}>
                {(this.points ?? []).map((point, key) => (
                  <TooltipItem
                    name={point.series.name}
                    color={point.color.toString()}
                    value={point.y}
                    key={key}
                  />
                ))}
              </div>
            )}
          </Tooltip>
        );
      },
    },
    xAxis: {
      ordinal: false,
      type: 'datetime',
      gridLineWidth: 0,
      dateTimeLabelFormats: {
        year: '%Y',
        month: '%Y/%m',
        day: '%m/%d',
        week: '%m/%d',
        hour: '%m/%d',
        minute: '%m/%d',
        second: '%m/%d',
        millisecond: '%m/%d',
      },
      events: {
        afterSetExtremes: function(event) {
          setRange &&
            setRange({
              min: new Date(event.min),
              max: new Date(event.max),
            });
        },
      },
      title: { style: { color: '#ffffff' } },
      labels: { style: { color: '#ffffff' } },
      tickColor: '#64748B',
      lineColor: '#64748B',
    },
    yAxis: {
      labels: {
        formatter: function(e): string {
          return typeof this.value === 'number'
            ? commaFormat(this.value / 10000, { numberOfDecimals: 0 })
            : '';
        },
        align: 'left',
        x: 3,
        y: 4,
        style: { color: '#ffffff' },
      },
      title: {
        text: '(万円)',
        align: 'high',
        offset: 0,
        rotation: 0,
        y: 5,
        style: { color: '#ffffff' },
      },
      gridLineColor: '#64748B',
      opposite: true,
    },
    chart: {
      className: rangeSelectorButtonAlignLeftChartClassName,
      animation: {
        duration: 0,
      },
      backgroundColor: '#475569',
      height: 310,
      marginBottom: 5,
      events: {
        redraw: function() {
          if (ref.current) {
            const chart = ref.current.chart;
            drawIcons({ chart, icons });
          }
        },
      },
    },
    plotOptions: {
      series: {
        ...marker,
        turboThreshold: 15000,
      },
      area: {
        events: {
          legendItemClick: (): false => false,
        },
        turboThreshold: 15000,
      },
      line: {
        events: {
          legendItemClick: (): false => false,
        },
        turboThreshold: 15000,
      },
    },
    series,
  });
  const [toolTipPosition, setToolTipPosition] = useState<{
    x: number;
    y: number;
    width: number;
    date: Date | null;
  }>({ x: 0, y: 0, width: 0, date: null });
  const [showToolTip, setShowToolTip] = useState<boolean>(false);

  useEffect(() => {
    setOptions(prev => {
      return merge({ ...prev, series }, options_ ?? {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options_, data]);

  useEffect(() => {
    setChartDataURL &&
      ref.current &&
      setChartDataURL(svgToDataURL(ref.current.chart.getSVG()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const drawIcons = ({
    chart,
  }: {
    chart: Highcharts.Chart;
    icons: IconOnChart[];
  }): void => {
    const iconSize = 15;
    const margin = 5;
    icons.forEach(({ icon }) => {
      if (icon.renderer) icon.destroy();
    });
    const xAxis = chart.xAxis[0];

    const incorrect = extraData?.find(it => it.type === 'IncorrectSign');
    if (!incorrect) return;
    const signSeries = incorrect.value
      .filter(it => it.date && it.value)
      .map(it => it.date);

    signSeries.forEach(date => {
      const x = xAxis.toPixels(arrangeDateForChart(date).getTime(), true);
      if (!chart.isInsidePlot(x, chart.plotTop)) return;
      const icon = chart.renderer
        .image(
          '/static/images/icon/icon-info.svg',
          x,
          chart.plotTop + chart.plotHeight - 7,
          iconSize,
          iconSize
        )
        .add();
      icon.toFront();
      const tooltipWidth = 150;
      let tx = x - tooltipWidth - margin;
      if (!chart.isInsidePlot(tx, chart.plotTop)) {
        tx = x + iconSize + margin;
      }

      icon.element.addEventListener('mouseover', () => {
        setToolTipPosition({
          x: tx,
          y: chart.chartHeight - chart.plotTop - chart.plotHeight,
          width: tooltipWidth,
          date: date,
        });
        setShowToolTip(true);
      });
      icon.element.addEventListener('mouseout', () => {
        setShowToolTip(false);
      });

      icons.push({ icon });
      setIcons(icons);
    });
  };

  const Tip = ({
    pos,
    show,
  }: {
    pos: { x: number; y: number; width: number; date: Date | null };
    show: boolean;
  }) => {
    return (
      <div
        className="p-2 absolute bg-white rounded-md shadow-md text-xs w-1/2 border border-gray-300"
        style={{
          display: show ? 'block' : 'none',
          zIndex: 999,
          left: pos.x,
          bottom: pos.y,
          maxWidth: pos.width,
        }}>
        <div className="flex flex-row">
          <div
            className="icon-info text-sm"
            id="icon-info"
            style={{ color: '#94A3B8' }}></div>
          <div className="ml-1 text-1.5xs">
            <div>
              集計が正常に完了しなかった可能性があるためこのデータは参考値です。
            </div>
            <div className="mt-1">
              {pos.date !== null
                ? `${pos.date.getFullYear()}/${pos.date.getMonth() +
                    1}/${pos.date.getDate()}`
                : '----/--/--'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <div className="relative">
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={options}
        ref={ref}
      />
      <Tip pos={toolTipPosition} show={showToolTip} />
    </div>
  );
};

export const usePreviewPager = (
  p: { tag: 'Render'; rendered: React.ReactElement } | { tag: 'NotRender' },
  backHome: () => void,
  toPreview: (element: React.ReactElement) => void
) => {
  useEffect(() => {
    if (p.tag === 'NotRender') {
      backHome();
      return;
    }
    toPreview(p.rendered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.tag]);
};

type PortfolioStuffingInternalProps = {
  assetHistory: AssetHistoryItemsProps[];
  asset: GetUserAssetResult;
  title: string;
};

const PortfolioStuffingChartForPreview: (
  props: ComponentProps<
    ChartForPreviewType<PortfolioStuffingChartProps['data']>
  >
) => React.ReactElement = ({ data, options, range, setChartDataURL }) => {
  return (
    <>
      <PortfolioStuffingChartInternal
        {...{
          data,
          forPreviewSetter: { tag: 'forInPreview', setChartDataURL },
          options: range
            ? merge(options, {
                xAxis: { min: range.min.getTime(), max: range.max.getTime() },
                legend: { margin: 10, itemDistance: 8, symbolPadding: -2 },
              } as Highcharts.Options)
            : options,
        }}
      />
    </>
  );
};

const assetHistoryToData = (
  assetHistory: AssetHistoryItemsProps[]
): PortfolioStuffingChartDatum[] => {
  const result = assetHistory.reduce(
    (acc, asset) => {
      return {
        stock: [
          ...acc.stock,
          { date: getDate(asset.day), value: asset.stockAmount },
        ],
        unlistedStock: [
          ...acc.unlistedStock,
          { date: getDate(asset.day), value: asset.unlistedStockAmount },
        ],
        investmentTrust: [
          ...acc.investmentTrust,
          { date: getDate(asset.day), value: asset.investmentTrustAmount },
        ],
        foreignStock: [
          ...acc.foreignStock,
          { date: getDate(asset.day), value: asset.uSStockAmount },
        ],
        etfEtn: [
          ...acc.etfEtn,
          { date: getDate(asset.day), value: asset.etfEtnAmount },
        ],
        foreignEtfEtn: [
          ...acc.foreignEtfEtn,
          { date: getDate(asset.day), value: asset.foreignEtfEtnAmount },
        ],
        reit: [
          ...acc.reit,
          { date: getDate(asset.day), value: asset.reitAmount },
        ],
        foreignReit: [
          ...acc.foreignReit,
          { date: getDate(asset.day), value: asset.foreignReitAmount },
        ],
        bond: [
          ...acc.bond,
          { date: getDate(asset.day), value: asset.bondAmount },
        ],
        foreignBond: [
          ...acc.foreignBond,
          { date: getDate(asset.day), value: asset.foreignBondAmount },
        ],
        eb: [...acc.eb, { date: getDate(asset.day), value: asset.ebAmount }],
        foreignEb: [
          ...acc.foreignEb,
          { date: getDate(asset.day), value: asset.foreignEbAmount },
        ],
        realEstate: [
          ...acc.realEstate,
          { date: getDate(asset.day), value: asset.realEstateAmount },
        ],
        roboAdvisor: [
          ...acc.roboAdvisor,
          { date: getDate(asset.day), value: asset.roboAdvisorAmount },
        ],
        margin: [
          ...acc.margin,
          { date: getDate(asset.day), value: asset.marginProfitOrLoss },
        ],
        cash: [
          ...acc.cash,
          { date: getDate(asset.day), value: asset.cashAmount },
        ],
        other: [
          ...acc.other,
          { date: getDate(asset.day), value: asset.otherAmount },
        ],
      };
    },
    {
      stock: [] as { date: Date; value: number | null }[],
      unlistedStock: [] as { date: Date; value: number | null }[],
      investmentTrust: [] as { date: Date; value: number | null }[],
      foreignStock: [] as { date: Date; value: number | null }[],
      etfEtn: [] as { date: Date; value: number | null }[],
      foreignEtfEtn: [] as { date: Date; value: number | null }[],
      reit: [] as { date: Date; value: number | null }[],
      foreignReit: [] as { date: Date; value: number | null }[],
      bond: [] as { date: Date; value: number | null }[],
      foreignBond: [] as { date: Date; value: number | null }[],
      eb: [] as { date: Date; value: number | null }[],
      foreignEb: [] as { date: Date; value: number | null }[],
      realEstate: [] as { date: Date; value: number | null }[],
      roboAdvisor: [] as { date: Date; value: number | null }[],
      margin: [] as { date: Date; value: number | null }[],
      cash: [] as { date: Date; value: number | null }[],
      other: [] as { date: Date; value: number | null }[],
    }
  );
  return [
    {
      assetTypeKey: AssetType['国内現物'],
      items: result.stock.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['未上場株式'],
      items: result.unlistedStock.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['投資信託'],
      items: result.investmentTrust.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['外国株式'],
      items: result.foreignStock.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['国内ETF・ETN'],
      items: result.etfEtn.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['外国ETF・ETN'],
      items: result.foreignEtfEtn.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['国内REIT'],
      items: result.reit.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['外国REIT'],
      items: result.foreignReit.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['国内債券'],
      items: result.bond.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['外国債券'],
      items: result.foreignBond.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['国内EB'],
      items: result.eb.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['外国EB'],
      items: result.foreignEb.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['不動産'],
      items: result.realEstate.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['ロボアドバイザー'],
      items: result.roboAdvisor.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['信用'],
      items: result.margin.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['預金_MRF'],
      items: result.cash.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
    {
      assetTypeKey: AssetType['その他'],
      items: result.other.map(value => {
        return {
          x: value.date,
          y: value.value,
        };
      }),
    },
  ];
};

export const PortfolioStuffingAssetChart = ({
  assetHistory,
  UserBrokerUpdateTime,
  asset,
  title,
  toPreview,
  backHome,
}: Omit<
  PortfolioStuffingProps,
  'getIsOutOfDate' | 'brokersInfo' | 'assetBundleType'
> &
  PortfolioStuffingInternalProps): React.ReactElement => {
  const data = useMemo(() => assetHistoryToData(assetHistory), [assetHistory]);

  const chartIncorrectData = useMemo(() => {
    const incorrectSign: ExtraTimeSeriesData = {
      type: 'IncorrectSign',
      value: assetHistory.map(it => {
        return {
          value: it.incorrect,
          date: getDate(it.day),
        } as TimeSeriesDatum;
      }),
    };
    return [incorrectSign];
  }, [assetHistory]);

  const props: UsePreviewProps<
    ChartPreviewState,
    PortfolioStuffingChartProps['data']
  > = {
    Chart: PortfolioStuffingChartForPreview,
    defaultState: defualtStateForHome(asset),
    extra: data,
    RenderedChartToElement,
    stateToOptions,
    StateController,
    onCancel: backHome,
    chartTitle: title,
  };

  const { preview, TwitterIconPreviewButton, setRange } = usePreview(props);
  usePreviewPager(preview(), backHome, toPreview);

  const isShowChart = !!dataToSeries(data).length;
  const View = useMemo(
    () => () => {
      return (
        <>
          <TwitterIconPreviewButton className="bg-bluegray-600" />
          <div className="pl-4 bg-bluegray-600">
            <PortfolioStuffingChartInternal
              data={data}
              extraData={chartIncorrectData}
              forPreviewSetter={{ tag: 'forGetRange', setRange }}
            />
          </div>
        </>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [UserBrokerUpdateTime, data, setRange]
  );

  return (
    <>
      <WithEmptyView
        View={View}
        EmptyView={() => {
          return (
            <>
              <div className="grid justify-center bg-bluegray-600 min-h-60">
                <img
                  src="/static/images/no-data-1.png"
                  alt="エリアチャートのイメージ"
                  className="m-auto p-5"
                  height="225"
                  width="350"
                />
              </div>
            </>
          );
        }}
        isEmpty={!isShowChart}
      />
      <div className="flex flex-row items-center relative z-10 -mt-2 mb-4 bg-bluegray-600 text-white">
        {UserBrokerUpdateTime}
      </div>
    </>
  );
};
